import { ORANGE_LEGACY_TEXT_STYLES as TEXT_STYLES } from '@junglescout/styles-library';
import { EdnaWrapper } from '@providers/index';
import * as AvatarRadix from '@radix-ui/react-avatar';
import { FC, memo } from 'react';
import styled from 'styled-components';
import { FallbackImage } from './FallbackImage';
import { AvatarProps, FontProps, SizeProps } from './types';

const Root = styled(AvatarRadix.Root)<
  SizeProps & { $showFallbackImage: boolean }
>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  overflow: hidden;
  user-select: none;
  width: ${(props) => props.size};
  height: ${(props) => props.size};
  border-radius: 100%;
  background-color: ${(props) =>
    props.$showFallbackImage
      ? props.theme.colors.grey200
      : props.theme.colors.grey600};
`;

const Image = styled(AvatarRadix.Image)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
  background-color: ${(props) => props.theme.colors.grey600};
`;

const Fallback = styled(AvatarRadix.Fallback)<FontProps>`
  ${TEXT_STYLES.BodyWhite};
  font-size: ${(props) => props.fontSize} !important;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.grey600};
`;

const firstLetter = (name: string): string => (name ? name[0] : '');

const fullNameInitials = (name?: string): string => {
  if (!name) {
    return '';
  }
  const names = name.split(' ');
  return (
    firstLetter(names[0]) +
    (names.length > 1 ? firstLetter(names[names.length - 1]) : '')
  );
};

const Avatar: FC<AvatarProps> = ({
  fullName,
  image,
  className,
  size = '28px',
  fontSize = '13px',
}: AvatarProps) => {
  const initials = fullNameInitials(fullName);
  const imageAlt = `Avatar image for ${fullName}`;

  const shouldShowFallbackImage = !image && !initials;

  return (
    <EdnaWrapper>
      <Root
        size={size}
        $showFallbackImage={shouldShowFallbackImage}
        className={className}
      >
        <Image src={image} alt={imageAlt} />
        {shouldShowFallbackImage ? (
          <FallbackImage size={size} />
        ) : (
          <Fallback fontSize={fontSize}>{initials}</Fallback>
        )}
      </Root>
    </EdnaWrapper>
  );
};

export default memo(Avatar);
